import { Switch, TAB_TYPE, useToast } from '@repo/design-system/index'
import { QIcon_checkbox_selected, QIcon_checkbox_unselected } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import classNames from 'classnames'
import { compareAsc } from 'date-fns'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import { CurrentUser } from '../../../../model/Common/common.interface'
import apis from '../../../../modules/apis'
import { ILessonPreparation } from '../../../../modules/apis/student/lesson'
import { axiosGet, axiosPost } from '../../../../modules/axiosHelper'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import { LOCALE_EN, LOCALE_KO } from '../../../../modules/i18n/config'
import { n_lesson_prep, s_preparation } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { DATA_REF_KEY } from '../../../../modules/ids/e2e/student'
import { lessonPreparationQueryApiV5 } from '../../../../modules/react-query/queryApis'
import { timezonedNow } from '../../../../modules/utils'
import LoadingSpinner from '../../../common/LoadingSpinner'
import ResizableImage from '../../../common/ResizableImage'
import { PREP_TABS } from '../../../materials/const'
import AnswerItem from './AnswerItem'
import MyAnswerHistory from './MyAnswerHistory'
import PeopleOpinions from './PeopleOpinions'
import QButton from './QButton'

// 답변 저장하기 버튼 클릭 시 - API 불러서 작성하고 있는 내용을 저장하고
//isEditable 을 false 로 설정해서 수정하기 상태로 갈 수 있도록
export const handleSubmitAnswer = async ({
  id,
  content,
  shareStatus,
  isEditable,
  setIsEditable,
  activeQuestionId,
  lessonId,
}) => {
  // content 아무것도 없을 수 없음 => 저장하기 버튼 비활성화임
  const payload = {
    coursekeyquestion_id: id,
    content: content,
    share_status: shareStatus, // 1: me, 0:public, 2: tutor and me
  }

  const response = await axiosPost(apis.lessons.preparation.answer(lessonId), payload)
  console.log(response.success)

  if (response.success) {
    // 해당 번째의 isEditable 을 false 로 만들어줌
    const updateIsEditable = [...isEditable]
    updateIsEditable.map((item, idx) => {
      if (Number(item.question_number) == activeQuestionId) {
        updateIsEditable[idx].student_answer.content = content
        updateIsEditable[idx].student_answer.share_status = shareStatus
        updateIsEditable[idx].editable = !item.editable
        setIsEditable(updateIsEditable)
      }
    })
  }
}

export const fetchData = async (lessonId) => {
  const response = await axiosGet(apis.lessons.preparation.getV5({ lessonId }))

  if (response) {
    return response
  }
}

// 답변 subTab
export const AnswerSubTab = ({ optionList, activeKey, setActiveKey, type }) => {
  return (
    <div className="border-b-1 z-[20] ml-[0px] lg:w-full">
      <div className="mx-[16px]">
        <div className="no-scrollbar flex">
          <div
            className={classNames(
              'bg-q-white border-q-gray-100 flex h-[52px] flex-row',
              type === TAB_TYPE.SCROLL ? 'justify-start ' : 'justify-evenly sm:justify-center'
            )}
          >
            {optionList.map((item, idx) => (
              <div
                key={idx}
                className={classNames(
                  'mb-[-1px] min-w-fit py-[13px] px-[8px]',
                  item.key === activeKey ? 'border-b-[2px] border-black text-black ' : 'cursor-pointer text-gray-300',
                  type === TAB_TYPE.SCROLL ? 'mx-[8px]' : 'mx-0 sm:mx-[68px] '
                )}
                onClick={() => {
                  if (item.key !== activeKey && setActiveKey) {
                    setActiveKey(item.key)
                  }
                }}
              >
                <div
                  className={classNames(
                    typeof item.label !== 'object' && 'Body1-16Bd leading-[26px]',
                    'flex items-center justify-start space-x-4'
                  )}
                >
                  <span className="text-16 font-bold text-black">{item.label}</span>
                  <span className="text-16 font-bold text-purple-500">{item.number}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * 다른 링글러들의 답변 정보 가져오는 함수.
 *
 */
export const getShareAnswer = async (answerId, setShareAnswers, setParticipantList) => {
  const response = await axiosGet(`${apis.lesson.getShareAnswerV5(answerId)}`)
  if (response) {
    console.log('getShareAnswer', response)
    setShareAnswers(response.answers)
    setParticipantList(response.participant_list)
  }
}

export interface QuestionSelectPanelPropsType {
  currentUser?: CurrentUser
  isReview?: boolean
  isLesson?: boolean
  isEditable: any
  setIsEditable: any
  setTabData?: any
  answer: any
  setAnswer: any
  selectedShareState: any
  setSelectedShareState: any
  activeQuestionId: any
  setActiveQuestionId: any
  id?: any
}

// 질문 내용
export const QuestionSelectPanel = ({
  currentUser,
  isReview,
  isLesson,
  isEditable,
  setIsEditable,
  setTabData,
  answer,
  setAnswer,
  selectedShareState,
  setSelectedShareState,
  activeQuestionId,
  setActiveQuestionId,
  id,
}: QuestionSelectPanelPropsType) => {
  const router = useRouter()
  const { t, locale } = useTranslation()
  const isMobile = useIsMobile(undefined, 640)
  const { sendClickEvent } = useEventTracking()

  console.log('locale +===========>', currentUser)

  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  // mobile webview 에서 lessonId 받아오기 => id
  const pathParams = lessonId !== undefined ? { lessonId } : { lessonId: Number(id) }
  const { data, isLoading } = useQuery<ILessonPreparation, AxiosError>({
    queryKey: ['lesson_question_items', lessonId, router?.asPath],
    queryFn: lessonPreparationQueryApiV5.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
    enabled: lessonId > 0 || id > 0,
  })

  let lesson, answers, participantList, questions
  const [shareAnswers, setShareAnswers] = useState([])
  const [shareParticipant, setParticipantList] = useState([])
  const [myAnswers, setMyAnswers] = useState([])
  const [questionKo, setQuestionKo] = useState([])

  // useEffect를 사용하여 data가 로드될 때 상태 초기화
  useEffect(() => {
    if (!isLoading && data) {
      lesson = data.lesson
      answers = lesson?.answers
      participantList = lesson?.participant_list
      questions = lesson?.coursekeyquestions_reordering

      setIsEditable(
        questions?.map((question) => ({
          ...question,
          editable: !question.student_answer?.content,
        }))
      )
      setParticipantList(lesson?.participant_list)
      setActiveQuestionId(questions && questions[0]?.question_number)
      setAnswer(
        isEditable?.find((item, idx) => Number(item.question_number) == activeQuestionId)?.student_answer?.content
      )
      setSelectedShareState(
        isEditable?.find((item, idx) => Number(item.question_number) == activeQuestionId)?.student_answer?.share_status
      )

      getShareAnswer(questions && questions[0]?.id, setShareAnswers, setParticipantList)

      // 학생 답안 전체
      setMyAnswers(
        !isReview
          ? lesson.preparation?.student_answers
            ? lesson.preparation?.student_answers.filter((item) => item.is_final)
            : []
          : lesson.preparation?.student_answers
          ? lesson.preparation?.student_answers
          : []
      )

      // 한국어 질문
      console.log(lesson.preparation?.coursekeyquestions)

      setQuestionKo(lesson.preparation?.coursekeyquestions ? lesson.preparation?.coursekeyquestions : [])

      setTabData(data.lesson)
    }
  }, [isLoading, data])

  useEffect(() => {
    const currentAnswer = isEditable?.find((item, idx) => Number(item.question_number) == activeQuestionId)
      ?.student_answer?.content
    const currentShareStatus = isEditable?.find((item, idx) => Number(item.question_number) == activeQuestionId)
      ?.student_answer?.share_status
    setAnswer(currentAnswer === undefined ? '' : currentAnswer)
    setSelectedShareState(currentShareStatus ? currentShareStatus : 0)
  }, [activeQuestionId])

  const { toastSuccess, toastError } = useToast()

  const [questionTranslate, setQuestionTranslate] = useState<boolean>(false)

  const [answerSubTabId, setAnswerSubTab] = useState<string>('my_answer')

  // tab 이동 시 answer 를 isEditable 에 저장하기 - 단 editable 바뀌지 않음
  const onChangeTab = () => {
    const updateIsEditable = [...isEditable]
    updateIsEditable.map((item, idx) => {
      if (item.question_number == activeQuestionId) {
        // if (!updateIsEditable[idx].student_answer) {
        //   return
        // }
        if (!updateIsEditable[idx].student_answer) {
          // 특정 인덱스의 `student_answer`가 없으면 빈 객체로 초기화합니다.
          updateIsEditable[idx].student_answer = {}
        }
        // `student_answer` 속성의 `content` 값을 지정합니다.
        updateIsEditable[idx].student_answer.content = answer
        updateIsEditable[idx].student_answer.share_status = selectedShareState
        setIsEditable(updateIsEditable)
      }
    })
  }

  const handleOpenOpinion = (answers) => {
    // track({
    //   location: isReview ? LOCATION.REVIEW : LOCATION.PREP,
    //   log_type: LOG_TYPE.COURSE.ANSWER_READ,
    //   log_category: LOG_CATEGORY.READ,
    //   lesson_id: lesson.id,
    // })
  }

  const validationCount = () => {
    if (lesson?.duration == 20) {
      return isEditable.filter((item, i) => item?.student_choice).length < 3
    } else {
      return true
    }
  }
  const isWebview = useIsWebview()
  const handleToggleQSelect = async (item) => {
    const targetItem = isEditable.find(
      (checkItem) => Number(checkItem.question_number) === Number(item?.question_number)
    )

    if (!targetItem?.student_choice && !validationCount()) {
      alert(t(s_preparation.you_can_only_select_up_to_three_questions))
      return
    }

    const filteredList = isEditable.filter(
      (checkItem) => Number(checkItem.question_number) !== Number(item?.question_number)
    )

    // [{ ...targetItem, student_choice: !targetItem.student_choice }, ...filteredList, ]
    const newChecked = !targetItem.student_choice
      ? [{ ...targetItem, student_choice: !targetItem.student_choice }, ...filteredList]
      : [...filteredList, { ...targetItem, student_choice: !targetItem.student_choice }]

    // tab question 요소 student_choice 별로 sorting 하기 위해
    const groupedByStudentChoice = newChecked.reduce((accumulator, item) => {
      const key = item.student_choice
      if (!accumulator[key]) {
        accumulator[key] = []
      }
      accumulator[key].push(item)
      return accumulator
    }, {})

    const sortedByQuestionNumber = Object.keys(groupedByStudentChoice).reduce((accumulator, key) => {
      const sortedGroup = groupedByStudentChoice[key].sort((a, b) => a.question_number - b.question_number)
      accumulator.push(...sortedGroup)
      return accumulator
    }, [])

    const payload = {
      coursekeyquestion_id: item.id,
      lesson_id: lessonId ? lessonId : id,
      checked: !targetItem.student_choice,
      platform: 'web',
    }
    const res = await axiosPost(`${apis.lesson.answerSelect()}`, payload)

    if (res.success) {
      setIsEditable(sortedByQuestionNumber)
      const selectedQuestions = newChecked.filter((item, i) => item.student_choice)

      const qCnt = data?.lesson?.duration == 20 ? 2 : 3
      if (selectedQuestions.length == qCnt) {
        toastSuccess(t(s_preparation.keyQeustionSelected(selectedQuestions.length)))
      }
    }

    // 데이터를 바꾸는 작업 필요
    const newData = await fetchData(lessonId)
    setTabData(newData?.lesson)
  }

  const isPast = () => {
    return compareAsc(timezonedNow(currentUser?.timezone), new Date(lesson?.start_time)) > 0
  }

  // 답변 subTab
  const AnswerSubTab = ({ optionList, activeKey, setActiveKey, type }) => {
    return (
      <div className="border-b-1 z-[20] ml-[0px] lg:w-full">
        <div className="mx-[16px]">
          <div className="no-scrollbar flex">
            <div
              className={classNames(
                'bg-q-white border-q-gray-100 flex h-[52px] flex-row',
                type === TAB_TYPE.SCROLL ? 'justify-start ' : 'justify-evenly sm:justify-center'
              )}
            >
              {optionList.map((item, idx) => (
                <div
                  key={idx}
                  className={classNames(
                    'mb-[-1px] min-w-fit py-[13px] px-[8px]',
                    item.key === activeKey ? 'border-b-[2px] border-black text-black ' : 'cursor-pointer text-gray-300',
                    type === TAB_TYPE.SCROLL ? 'mx-[8px]' : 'mx-0 sm:mx-[68px] '
                  )}
                  onClick={() => {
                    if (item.key !== activeKey && setActiveKey) {
                      setActiveKey(item.key)
                    }

                    console.log(item.key)

                    // click event
                    let object_title
                    switch (item.key) {
                      case 'my_answer':
                        object_title = PREP_TABS.MY_ANSWER
                        break
                      case 'other_answer':
                        object_title = PREP_TABS.OTHER_ANSWER
                        break
                      default:
                        break
                    }
                    sendClickEvent({ button_name: 'lesson_prep_tab', object_title })
                  }}
                >
                  <div
                    className={classNames(
                      typeof item.label !== 'object' && 'Body1-16Bd leading-[26px]',
                      'flex items-center justify-start space-x-4'
                    )}
                  >
                    <span className="text-16 font-bold text-black">{item.label}</span>
                    <span className="text-16 font-bold text-purple-500">{item.number}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  interface AnswerBodyProps {
    activeKey: string // You can replace 'string' with the actual type of activeKey
  }

  const AnswerBody = React.memo(({ activeKey }: AnswerBodyProps) => {
    const filteredAnswers = myAnswers.filter((item) => item.question_number == activeQuestionId)

    return (
      <div>
        {activeKey === 'my_answer' ? (
          <MyAnswerHistory isEditable={isEditable} myAnswers={filteredAnswers} setMyAnswers={setMyAnswers} id={id} />
        ) : (
          <PeopleOpinions
            answers={shareAnswers}
            participantList={shareParticipant}
            handleOpenOpinion={handleOpenOpinion}
            tutorAnswerComponent={null}
          />
        )}
      </div>
    )
  })

  AnswerBody.displayName = 'AnswerBody'

  const optionList = [
    {
      key: 'my_answer',
      label: t(n_lesson_prep.answer_my),
      number: myAnswers.length,
    },
    {
      key: 'other_answer',
      label: t(n_lesson_prep.answer_other),
      number: shareAnswers?.length,
    },
  ]

  console.log(
    'question ko ==============>',
    questionKo?.find((item) => item.question_number == activeQuestionId)
  )

  return (
    <div className="mx-[32px] text-black">
      {isLoading ? (
        <LoadingSpinner lg show primary />
      ) : isEditable && isEditable.length > 0 ? (
        // 질문 선택
        <div>
          <div className="mb-[24px] flex gap-x-8 overflow-x-auto sm:flex-wrap sm:gap-y-8">
            {isEditable.map((item, index) => (
              <div
                data-ref={DATA_REF_KEY.btn_question}
                key={`${index}_${item.id}`}
                onClick={(e) => {
                  getShareAnswer(item.id, setShareAnswers, setParticipantList)
                }}
              >
                <QButton
                  key={`${index}_${item.id}`}
                  number={item?.question_number}
                  checked={isEditable}
                  activeQuestionId={activeQuestionId}
                  setActiveQuestionId={setActiveQuestionId}
                  onChangeTab={onChangeTab}
                />
              </div>
            ))}
          </div>
          {/* 질문카드 */}
          <div className="mt-[4px]">
            <div key={`question-${activeQuestionId}`}>
              <div>
                <div>
                  <div className="flex">
                    {!isLesson && !isPast() && (
                      <div
                        data-ref={DATA_REF_KEY.btn_select_question}
                        style={{ width: 'fit-content' }}
                        onClick={(e) =>
                          handleToggleQSelect(isEditable.find((item) => item.question_number === activeQuestionId))
                        }
                        className="text-sBody mb-[8px] flex cursor-pointer items-center space-x-4 bg-gray-50 px-12 py-8"
                      >
                        {isEditable.find((qItem) => qItem.question_number === activeQuestionId)?.student_choice ? (
                          <QIcon_checkbox_selected className="h-20 w-20" />
                        ) : (
                          <QIcon_checkbox_unselected className="h-20 w-20" />
                        )}
                        <>
                          {isMobile ? (
                            <span className="text-16 font-bold text-black">{`Q${activeQuestionId}`}</span>
                          ) : (
                            <span className="text-14 font-medium text-black">{t(s_preparation.select_question)}</span>
                          )}
                        </>
                      </div>
                    )}
                    {(locale == LOCALE_KO || locale == LOCALE_EN) && (
                      <>
                        {questionKo?.find((item) => item.question_number == activeQuestionId)?.content_main_ko && (
                          <div className="ml-auto flex items-center space-x-4">
                            <span className="text-14 font-medium text-gray-700">{t(n_lesson_prep.view_kr)}</span>
                            <div
                              onClick={() => {
                                // 질문 한글로 보기 toggle event tracking
                                sendClickEvent({
                                  button_name: 'lesson_question_lang_toggle',
                                  object_title: !questionTranslate ? 'on' : 'off',
                                })
                              }}
                            >
                              <Switch checked={questionTranslate} setChecked={setQuestionTranslate} />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="text-h2 mb-[8px] font-bold">
                    {isEditable.find((item) => item.question_number === activeQuestionId)?.content_main}
                  </div>
                  {questionTranslate && (
                    <div className="mb-[16px] text-[16px] font-medium text-gray-500">
                      {questionKo?.find((item) => item.question_number == activeQuestionId)?.content_main_ko}
                    </div>
                  )}
                  {isEditable.find((item) => item.question_number === activeQuestionId)?.img_url_1 &&
                    isEditable.find((item) => item.question_number === activeQuestionId)?.img_url_1 !== '' && (
                      <ResizableImage
                        url={isEditable.find((item) => item.question_number === activeQuestionId)?.img_url_1}
                      />
                    )}
                </div>
                <div style={{ height: 1 }} className="my-[20px] bg-gray-200"></div>
                <div>
                  {/* 내 답변 */}
                  <AnswerItem
                    answer={answer}
                    setAnswer={setAnswer}
                    isEditable={isEditable}
                    setIsEditable={setIsEditable}
                    activeQuestionId={Number(activeQuestionId)}
                    lessonId={lessonId != undefined ? lessonId : id}
                    handleSubmitAnswer={handleSubmitAnswer}
                    onBlur={onChangeTab}
                    currentUser={currentUser}
                    isReview={isReview}
                    isLesson={isLesson}
                    isPast={isPast}
                    selectedShareState={selectedShareState}
                    setSelectedShareState={setSelectedShareState}
                  />
                </div>
                {/** webview 일때는 회색 선이 없어야 함 */}
                {!isWebview && <div style={{ height: 8 }} className="mt-[40px] bg-gray-100"></div>}
                {isWebview && <div className="h-[36px]"></div>}
              </div>
            </div>
          </div>
          {/** 답변 확인 */}
          <div>
            <AnswerSubTab
              optionList={optionList}
              activeKey={answerSubTabId}
              setActiveKey={setAnswerSubTab}
              type={TAB_TYPE.SCROLL}
            />
            <AnswerBody activeKey={answerSubTabId} />
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="h-[50px]"></div>
    </div>
  )
}
